import React from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../lib/convex';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { DEFAULT_AUTHOR_BIO } from '@/constants/authorBio';
import { 
  Edit, 
  Plus, 
  Trash2, 
  Loader2, 
  Eye, 
  Search,
  Calendar, 
  Tag,
  FileText,
  Check,
  X,
  User,
  Link as LinkIcon,
  Upload
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Footerdemo } from '../ui/footer-section';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

export default function BlogManager() {
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const isAdmin = useQuery(api.blog.checkAdminStatus);
  const blogPosts = useQuery(api.blog.getAdminBlogPosts);
  const updateBlogPost = useMutation(api.blog.updateBlogPost);
  
  const [searchQuery, setSearchQuery] = React.useState('');
  const [isAuthorBioOpen, setIsAuthorBioOpen] = React.useState(false);
  const [authorBio, setAuthorBio] = React.useState({
    name: '',
    imageUrl: '',
    headline: '',
    tagline: '',
    description: '',
    link1: { text: '', url: '' },
    link2: { text: '', url: '' }
  });
  
  // Get first post to check for existing author bio
  React.useEffect(() => {
    if (blogPosts && blogPosts.length > 0 && blogPosts[0].authorBio) {
      setAuthorBio(blogPosts[0].authorBio);
    }
  }, [blogPosts]);

  // Filter posts based on search query
  const filteredPosts = React.useMemo(() => {
    if (!blogPosts) return [];
    
    if (!searchQuery.trim()) return blogPosts;
    
    const query = searchQuery.toLowerCase();
    return blogPosts.filter(post => 
      post.title.toLowerCase().includes(query) ||
      post.tags.some(tag => tag.toLowerCase().includes(query))
    );
  }, [blogPosts, searchQuery]);

  const handleAuthorBioSave = async () => {
    try {
      // Update all posts with the new author bio
      for (const post of blogPosts || []) {
        await updateBlogPost({
          postId: post._id,
          authorBio: DEFAULT_AUTHOR_BIO
        });
      }
      
      setIsAuthorBioOpen(false);
      toast({
        title: "Success",
        description: "Author bio updated for all posts",
      });
    } catch (error) {
      console.error('Error updating author bio:', error);
      toast({
        title: "Error",
        description: "Failed to update author bio",
        variant: "destructive",
      });
    }
  };

  const handleAuthorImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      // In a real app, you would upload to a storage service
      // For this example, we'll use a data URL
      const reader = new FileReader();
      reader.onload = () => {
        setAuthorBio(prev => ({
          ...prev,
          imageUrl: reader.result as string
        }));
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Error",
        description: "Failed to upload image",
        variant: "destructive",
      });
    }
  };

  // If not an admin, show access denied
  if (isAdmin === false) {
    return (
      <div className="min-h-screen flex flex-col pt-16">
        <div className="flex-1 max-w-7xl mx-auto px-4 py-8">
          <div className="p-8 bg-red-50 border border-red-200 rounded-lg text-center">
            <h1 className="text-2xl font-bold text-red-700 mb-4">Access Denied</h1>
            <p className="text-red-600">You don't have permission to access this area.</p>
          </div>
        </div>
        <Footerdemo />
      </div>
    );
  }
  
  // Show loading state
  if (blogPosts === undefined && isAdmin !== false) {
    return (
      <div className="min-h-screen flex flex-col pt-16">
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
        <Footerdemo />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex flex-col pt-16">
      <div className="flex-1 max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Blog Posts</h1>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={() => setIsAuthorBioOpen(true)}
            >
              <User className="w-4 h-4 mr-2" />
              Author Bio
            </Button>
            <Button 
              onClick={() => navigate('/admin/blog/new')}
            >
              <Plus className="w-4 h-4 mr-2" />
              New Post
            </Button>
          </div>
        </div>
        
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Search posts by title or tag..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full whitespace-nowrap">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tags
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredPosts?.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-10 text-center text-gray-500">
                      <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      {searchQuery ? 'No posts match your search' : 'No blog posts yet'}
                    </td>
                  </tr>
                ) : (
                  filteredPosts?.map((post) => (
                    <tr key={post._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            {post.imageUrl ? (
                              <img
                                className="h-10 w-10 rounded object-cover"
                                src={post.imageUrl}
                                alt=""
                              />
                            ) : (
                              <div className="h-10 w-10 rounded bg-gray-200 flex items-center justify-center">
                                <FileText className="h-5 w-5 text-gray-500" />
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {post.title}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {post.isPublished ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            <Check className="w-3 h-3 mr-1" />
                            Published
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            <X className="w-3 h-3 mr-1" />
                            Draft
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1 text-gray-400" />
                          {post.publishedAt 
                            ? formatDistanceToNow(post.publishedAt, { addSuffix: true })
                            : formatDistanceToNow(post.createdAt, { addSuffix: true })}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div className="flex flex-wrap gap-1">
                          {post.tags.slice(0, 3).map((tag, index) => (
                            <span 
                              key={index}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              <Tag className="w-3 h-3 mr-1" />
                              {tag}
                            </span>
                          ))}
                          {post.tags.length > 3 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                              +{post.tags.length - 3}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/blog/${post.slug}`)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/admin/blog/edit/${post._id}`)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Author Bio Dialog */}
      <Dialog open={isAuthorBioOpen} onOpenChange={setIsAuthorBioOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Author Bio Settings</DialogTitle>
            <DialogDescription>
              Set your author bio information that will appear at the bottom of all blog posts.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Author Image</Label>
              <div className="flex items-start gap-4">
                {authorBio.imageUrl && (
                  <img 
                    src={authorBio.imageUrl} 
                    alt="Author"
                    className="w-24 h-24 rounded-full object-cover"
                  />
                )}
                <div className="flex-1">
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAuthorImageUpload}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {authorBio.imageUrl ? 'Change Image' : 'Upload Image'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Name</Label>
              <Input
                value={authorBio.name}
                onChange={(e) => setAuthorBio(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Your full name"
              />
            </div>

            <div className="grid gap-2">
              <Label>Headline</Label>
              <Input
                value={authorBio.headline}
                onChange={(e) => setAuthorBio(prev => ({ ...prev, headline: e.target.value }))}
                placeholder="e.g., Founder & CEO"
              />
            </div>

            <div className="grid gap-2">
              <Label>Tagline</Label>
              <Input
                value={authorBio.tagline}
                onChange={(e) => setAuthorBio(prev => ({ ...prev, tagline: e.target.value }))}
                placeholder="A short description or motto"
              />
            </div>

            <div className="grid gap-2">
              <Label>Description</Label>
              <textarea
                value={authorBio.description}
                onChange={(e) => setAuthorBio(prev => ({ ...prev, description: e.target.value }))}
                placeholder="A brief bio about yourself"
                className="min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

            <div className="grid gap-4">
              <Label>Links (Optional)</Label>
              <div className="grid gap-2">
                <div className="flex gap-2">
                  <Input
                    value={authorBio.link1.text}
                    onChange={(e) => setAuthorBio(prev => ({ 
                      ...prev, 
                      link1: { ...prev.link1, text: e.target.value }
                    }))}
                    placeholder="Link 1 Text"
                  />
                  <Input
                    value={authorBio.link1.url}
                    onChange={(e) => setAuthorBio(prev => ({ 
                      ...prev, 
                      link1: { ...prev.link1, url: e.target.value }
                    }))}
                    placeholder="Link 1 URL"
                  />
                </div>
                <div className="flex gap-2">
                  <Input
                    value={authorBio.link2.text}
                    onChange={(e) => setAuthorBio(prev => ({ 
                      ...prev, 
                      link2: { ...prev.link2, text: e.target.value }
                    }))}
                    placeholder="Link 2 Text"
                  />
                  <Input
                    value={authorBio.link2.url}
                    onChange={(e) => setAuthorBio(prev => ({ 
                      ...prev, 
                      link2: { ...prev.link2, url: e.target.value }
                    }))}
                    placeholder="Link 2 URL"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={handleAuthorBioSave}>Save Author Bio</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footerdemo />
    </div>
  );
}